import React, { ReactElement, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import OrderMethodInfo from './OrderMethodInfo';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { DropoffAction, ORDER_METHODS, PaymentMethod } from '../../services/exports/Constants';
import useTheme from '../../hooks/ui/useTheme';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { SectionType } from './sections/CheckoutInfoBag';
import useAuth from '../../hooks/global/useAuth';
import collect from 'collect.js';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import usePaymentFlow from '../../hooks/payment/usePaymentFlow';
import PaymentDetails from '../payment/payment-methods/PaymentDetails';
import { Checkbox, Radio } from '@mui/material';

interface Props {
  toggleSection: (section: SectionType, open: boolean) => any;
}

interface SectionProps {
  title: string;
  children: any;
  onChange?: () => any;
  hideBorder?: boolean;
}

export default function OrderInfo(props: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:OrderInfo' });

  const { toggleSection } = props;

  const theme = useTheme();
  const { isGuest } = useAuth();
  const { toggleOrderMethodModal } = useAppMethods();
  const { selectedPaymentMethod, paymentOptions, findSavedPaymentMethod } = usePaymentFlow();
  const { data, localData, update } = useOrderFlow();

  const savedPaymentMethod = useMemo(
    () => findSavedPaymentMethod(selectedPaymentMethod),
    [selectedPaymentMethod, paymentOptions],
  );

  const orderDetailsInfo = useMemo(
    () =>
      collect()
        .when(localData.method === ORDER_METHODS.DELIVERY, (collection) =>
          collection
            .merge([localData.delivery_doorbell_name, localData.delivery_company_name, localData.customer_note])
            .whereNotNull(),
        )
        .when(localData.method === ORDER_METHODS.ROOM_SERVICE, (collection) =>
          collection.merge([localData.order_instructions]).whereNotNull(),
        )
        .implode(', '),
    [
      localData.method,
      localData.delivery_doorbell_name,
      localData.delivery_company_name,
      localData.customer_note,
      localData.order_instructions,
    ],
  );

  const fullName = collect([localData.customer_first_name, localData.customer_last_name])
    .filter((item) => Boolean(item))
    .implode(' ');

  const invoiceInfo = collect([
    localData.invoice_company_name,
    localData.invoice_tax_id,
    localData.invoice_address,
    localData.invoice_email,
  ])
    .filter((item) => Boolean(item))
    .implode(', ');

  useEffect(() => {
    !localData.delivery_dropoff_action && update('delivery_dropoff_action', DropoffAction.RingDoorBell);
  }, []);

  const openOrderMethodInfoSection = () => toggleSection(SectionType.order_method_details, true);
  const openCustomerInfoSection = () => toggleSection(SectionType.customer_info, true);
  const openPaymentInfoSection = () => toggleSection(SectionType.payment_info, true);
  const openInvoiceInfoSection = () => toggleSection(SectionType.invoice_info, true);

  const Row = ({ title, children, onChange, hideBorder }: SectionProps): ReactElement => (
    <div className={classnames('py-sm', { 'border-t': !hideBorder })}>
      <div className="flex justify-between">
        <p className="text-sm text-gray-700">{title}</p>
        {onChange && (
          <button onClick={onChange} className="text-xs text-brand-primary underline">
            {t('buttons.change')}
          </button>
        )}
      </div>
      <div className="mt-xs">{children}</div>
    </div>
  );

  const title = useMemo(
    () =>
      ({
        [ORDER_METHODS.ROOM_SERVICE]: `sections.order_method.titles.${data.method}.${localData.room_service_location?.type}`,
      })[data.method] ?? `sections.order_method.titles.${data.method}`,
    [data.method, localData.room_service_location?.type],
  );

  return (
    <div className="border px-md rounded-2xl">
      <Row title={t(title)} hideBorder onChange={() => toggleOrderMethodModal({ openModal: true })}>
        <OrderMethodInfo />
      </Row>
      {localData.method === ORDER_METHODS.DELIVERY && (
        <Row title={t('sections.delivery_info.title')} onChange={orderDetailsInfo ? openOrderMethodInfoSection : null}>
          {Object.values(DropoffAction).map((item) => (
            <button
              className="flex flex-row w-fit items-center mt-xs"
              onClick={() => update('delivery_dropoff_action', item)}
              key={`dropoff-action-${item}`}
            >
              <Radio
                checked={localData.delivery_dropoff_action === item}
                sx={{
                  color: theme.color.black.DEFAULT,
                  '&.Mui-checked': {
                    color: theme.color.black.DEFAULT,
                  },
                }}
                key={`delivery-dropoff-action-${item}`}
              />
              <p className="ml-xs my-auto text-sm">{t(`form.dropoff_action.options.${item}`)}</p>
            </button>
          ))}
          {(isGuest || !orderDetailsInfo) && (
            <button className="underline text-sm mt-xs" onClick={openOrderMethodInfoSection}>
              {t('buttons.add_delivery_note')}
            </button>
          )}
          {Boolean(orderDetailsInfo) && <p className="text-sm mt-xs">{orderDetailsInfo}</p>}
        </Row>
      )}
      {!isGuest && (
        <Row title={t('sections.customer_info.title')} onChange={openCustomerInfoSection}>
          {fullName && <p className="text-sm">{fullName}</p>}
          {Boolean(localData.customer_email) && <p className="text-sm">{localData.customer_email}</p>}
          {Boolean(localData.customer_phone_number) && (
            <p className="text-sm">{formatPhoneNumberIntl(localData.customer_phone_number)}</p>
          )}
        </Row>
      )}
      {data.total > 0 && selectedPaymentMethod && (
        <Row title={t('sections.payment.title')} onChange={openPaymentInfoSection}>
          <PaymentDetails
            {...savedPaymentMethod}
            payment_method_id={selectedPaymentMethod}
            hideIcons={selectedPaymentMethod === PaymentMethod.Card && !savedPaymentMethod}
            classes={{
              icon: 'h-5',
            }}
          />
        </Row>
      )}
      {data.total > 0 && !isGuest && (
        <Row title={t('sections.request_invoice.title')} onChange={openInvoiceInfoSection}>
          <div className="flex">
            <Checkbox
              checked={localData.is_receipt_required}
              onChange={(e) => update('is_receipt_required', e.target.checked)}
              id="is-receipt-required-duplicate"
            />
            <label htmlFor="is-receipt-required-duplicate" className="text-sm ml-xs my-auto">
              {t('sections.request_invoice.form.is_receipt_required.label')}
            </label>
          </div>
          {localData.is_receipt_required && Boolean(invoiceInfo) && <p className="text-sm mt-xs">{invoiceInfo}</p>}
        </Row>
      )}
    </div>
  );
}
